import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import useSEO from '../hooks/useSEO';
import { useInView } from 'react-intersection-observer';
import { FiMail, FiPhone, FiMapPin, FiClock } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import ConsultationForm from '../components/ConsultationForm';
import { handleHashNavigation } from '../utils/navigation';

const Contact = () => {
  useSEO({
    title: 'Enroll Now & Book Consultation',
    description: 'Ready to start your health transformation? Enroll now for custom weight loss, weight gain, and disease management diet plans by nutritionist Deeksha Rajput.',
    keywords: 'book dietitian consultation, enroll diet plan, contact Dfitclub, Deeksha Rajput contact',
  });

  const [heroRef, heroInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [formRef, formInView] = useInView({ threshold: 0.1, triggerOnce: true });

  // Handle hash navigation when page loads
  useEffect(() => {
    console.log('🔍 Contact page loaded, checking for hash navigation');
    handleHashNavigation();
  }, []);

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Phone',
      details: '+91-9355353459',
      action: 'tel:+919355353459'
    },
    {
      icon: FiMail,
      title: 'Email',
      details: 'contact@dfitclub.com',
      action: 'mailto:contact@dfitclub.com'
    },
    {
      icon: FaWhatsapp,
      title: 'WhatsApp',
      details: 'Chat with us instantly',
      action: 'https://wa.me/919355353459'
    },
    {
      icon: FiClock,
      title: 'Consultation Hours',
      details: 'Mon-Sat: 9AM-7PM IST',
      action: null
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section ref={heroRef} className="py-12 bg-gradient-to-br from-primary-50 to-white">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Get in <span className="text-visible-orange">Touch</span>
            </h1>
            <p className="text-lg text-secondary-600 mb-6">
              Ready to start your health transformation? Enroll now or reach out with any questions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-10 bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="card p-4 text-center card-hover"
              >
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <info.icon className="w-5 h-5 text-primary-600" />
                </div>
                <h3 className="font-semibold text-secondary-900 mb-2 text-sm">{info.title}</h3>
                {info.action ? (
                  <a
                    href={info.action}
                    target={info.action.startsWith('http') ? '_blank' : '_self'}
                    rel={info.action.startsWith('http') ? 'noopener noreferrer' : ''}
                    className="text-primary-600 hover:text-primary-700 transition-colors text-sm"
                  >
                    {info.details}
                  </a>
                ) : (
                  <p className="text-secondary-600 text-sm">{info.details}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section id="consultation" ref={formRef} className="py-10 bg-gray-50">
        <div className="container-max">
          <div className="max-w-2xl mx-auto">
            {/* Consultation Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={formInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <div className="card p-6 md:p-8">
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  Enroll Now
                </h2>
                <p className="text-secondary-600 mb-6 text-sm">
                  Get personalized guidance from Deeksha Rajput. Fill out the form below to enroll.
                </p>
                <ConsultationForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 bg-white">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-3">
              Frequently Asked <span className="text-visible-orange">Questions</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {[
              {
                question: "How do I book a consultation?",
                answer: "Simply fill out the enrollment form above or click the 'Enroll Now' button. We'll contact you within 24 hours to schedule your free session."
              },
              {
                question: "Are consultations really free?",
                answer: "Yes! Your first consultation is completely free. This helps us understand your goals and determine the best program for you."
              },
              {
                question: "Do you offer online consultations?",
                answer: "Absolutely! We provide virtual consultations via video calls, making it convenient for clients worldwide."
              },
              {
                question: "How long does a typical consultation last?",
                answer: "Initial consultations typically last 45-60 minutes. This gives us enough time to understand your goals, health history, and lifestyle."
              },
              {
                question: "What should I prepare for my consultation?",
                answer: "Come with your health goals, any medical history, current eating habits, and questions about nutrition and lifestyle changes."
              },
              {
                question: "How quickly will I see results?",
                answer: "Results vary by individual, but most clients see initial changes within 2-4 weeks with consistent follow-through on the program."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="card p-4"
              >
                <h3 className="font-semibold text-secondary-900 mb-2 text-sm">{faq.question}</h3>
                <p className="text-secondary-600 text-xs leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;